import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import AssignSGLModal from '@/components/AssignSGLModal/AssignSGLModal'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { LeadDetailsDto } from 'api/Leads/Types/LeadDetailsDto'
import { UpdateLead } from 'api/Leads/Types/UpdateLead'
import { VerifiableActionDto } from 'api/Leads/Types/VerifiableActionDto'
import { Spinner } from 'components/Spinner'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchClientLanguagesAction,
  fetchClientTypesAction,
  fetchLeadSalesStatusAction,
  fetchTickmillProductsAction,
} from 'store/clients/clients.actions'
import { fetchCountriesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { dateComparator } from 'utils/date'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'

import ClientAreaAuditLogModal from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { ClientsApiClient } from '../../../../utils/api/clientsApi'
import { HubSpotApiClient } from '../../../../utils/api/hubSpotApi'
import { IdentityApiClient } from '../../../../utils/api/identityApi'
import { LeadNavigation } from '../Navigation/LeadNavigation'
import { LeadDetailsForm } from './LeadDetailsForm'
import { LeadTab } from './LeadSlug'
import { LeadVerificationsPage } from './LeadVerificationsPage'

interface Props {
  data: LeadDetailsDto

  forceRefresh(): void
}

const useMapStateToProps = () => ({
  countries: useAppSelector((state) => state.identity.countries) ?? [],
  languages: useAppSelector((state) => state.clients.language) ?? [],
  clientTypes: useAppSelector((state) => state.clients.types) ?? [],
  salesStatuses: useAppSelector((state) => state.clients.leadSalesStatus) ?? [],
  tickmillProducts: useAppSelector((state) => state.clients.tickmillProducts) ?? [],
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  return {
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchClientLanguages: async () => dispatch(fetchClientLanguagesAction(clientsApiClient)),
    fetchClientTypes: async () => dispatch(fetchClientTypesAction(clientsApiClient)),
    fetchLeadSalesStatus: async (apiClient: LeadsApiClient) =>
      dispatch(fetchLeadSalesStatusAction(apiClient)),
    fetchTickmillProducts: async () => dispatch(fetchTickmillProductsAction(clientsApiClient)),
  }
}

const LeadDetailsPage: React.FC<Props> = ({ data, forceRefresh }) => {
  const [auth] = useContext(AuthSessionContext)
  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)
  const [assignSGLVisible, setAssignSGLVisible] = useState<boolean>(false)
  const [isVerificationsModalOpen, setVerificationsModalOpen] = useState<boolean>(false)
  const [isEdit, setEdit] = useState<boolean>(false)
  const setToast = useContext(ToastContext)
  const leadsApiClient = useApiClient(LeadsApiClient)
  const hubSpotApiClient = useApiClient(HubSpotApiClient)

  const { countries, languages, clientTypes, salesStatuses, tickmillProducts } =
    useMapStateToProps()
  const permissions = auth?.permissions || []

  const sortedCountries = useMemo(
    () => [...countries].sort((f1, f2) => stringComparator(f1.name, f2.name)),
    [countries]
  )
  const {
    fetchCountries,
    fetchClientLanguages,
    fetchClientTypes,
    fetchLeadSalesStatus,
    fetchTickmillProducts,
  } = useMapDispatchToProps()

  const fetchData = useCallback(async () => {
    if (data?.id) {
      return leadsApiClient.getLeadVerifiableActions(data.id)
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: leadVerifiableActions, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    ;(async () => {
      await fetchCountries()
      await fetchClientLanguages()
      await fetchClientTypes()
      if (isEmpty(salesStatuses)) {
        await fetchLeadSalesStatus(leadsApiClient)
      }
      if (isEmpty(tickmillProducts)) {
        await fetchTickmillProducts()
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const syncHubspot = async () => {
    await hubSpotApiClient.syncHubSpotForLeads({ leadIds: [data.id] })
    setToast(successToast('HubSpot synced.'))
    forceRefresh()
  }

  const onSubmit = async (values: UpdateLead) => {
    if (!hasPermission(permissions, PermissionsEnum.CanEditLeads))
      return setToast(errorToast('Permission denied.'))
    await leadsApiClient.updateLead(values.id, values)
    setToast(successToast('Lead updated.'))
  }

  if (isLoading || !leadVerifiableActions) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {assignSGLVisible && (
        <AssignSGLModal
          isVisible={assignSGLVisible}
          leadId={data.id}
          onClose={() => setAssignSGLVisible(false)}
        />
      )}
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          leadId={data.id}
        />
      )}
      {isVerificationsModalOpen && (
        <Dialog
          open={isVerificationsModalOpen}
          onOpenChange={(open) => !open && setVerificationsModalOpen(false)}
        >
          <DialogContent size='xl'>
            <LeadVerificationsPage data={data} />
          </DialogContent>
        </Dialog>
      )}
      <LeadNavigation data={data} isEdit={false} tab={LeadTab.DETAILS} />
      <LeadDetailsForm
        data={data}
        leadVerifiableAction={pickLastVerifiableAction(leadVerifiableActions)}
        permissions={permissions}
        clientTypes={clientTypes}
        user={auth?.user}
        countries={sortedCountries}
        tickmillProducts={tickmillProducts}
        languages={languages}
        salesStatuses={salesStatuses}
        isConverted={data.isConverted}
        setAuditLogModal={setAuditLogVisible}
        setAssignSGLModal={setAssignSGLVisible}
        syncHubspot={syncHubspot}
        onSubmit={onSubmit}
        showVerificationsModal={() => setVerificationsModalOpen(true)}
        isEdit={isEdit}
        setEdit={setEdit}
      />
    </React.Fragment>
  )
}

const pickLastVerifiableAction = (
  verifiableActions: VerifiableActionDto[] = []
): VerifiableActionDto => {
  const [verifiableAction] = verifiableActions
    .sort((a, b) => {
      return dateComparator(a.created, b.created)
    })
    .reverse()

  return verifiableAction || {}
}

export default LeadDetailsPage
